import {
  type RowData,
  type Table as TableInstance,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "~/atoms/pagination";
import { allPageSizeParamValue } from "~/helpers/pagination";

interface TablePaginationProps<TData extends RowData> {
  table: TableInstance<TData>;
  currentPage: number;
  visiblePageNumbers: number[];
  pageSizeValue: string;
  onPageSizeChange: (value: string) => void;
}

export const TablePagination = <TData extends RowData>({
  table,
  currentPage,
  visiblePageNumbers,
  pageSizeValue,
  onPageSizeChange,
}: TablePaginationProps<TData>) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-end gap-4">
      <Pagination className="w-auto justify-start">
        <PaginationContent className="gap-2">
          <PaginationItem>
            <PaginationPrevious
              href="#"
              aria-disabled={!table.getCanPreviousPage()}
              className={
                table.getCanPreviousPage()
                  ? undefined
                  : "pointer-events-none opacity-50"
              }
              onClick={(event) => {
                event.preventDefault();
                table.previousPage();
              }}
            />
          </PaginationItem>
          {visiblePageNumbers.map((pageNumber) => (
            <PaginationItem key={pageNumber}>
              <PaginationLink
                href="#"
                isActive={pageNumber === currentPage}
                onClick={(event) => {
                  event.preventDefault();
                  table.setPageIndex(pageNumber - 1);
                }}
              >
                {pageNumber}
              </PaginationLink>
            </PaginationItem>
          ))}
          <PaginationItem>
            <PaginationNext
              href="#"
              aria-disabled={!table.getCanNextPage()}
              className={
                table.getCanNextPage()
                  ? undefined
                  : "pointer-events-none opacity-50"
              }
              onClick={(event) => {
                event.preventDefault();
                table.nextPage();
              }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      <select
        className="border-input bg-background h-9 rounded-md border px-3 py-1 text-sm"
        value={pageSizeValue}
        onChange={(event) => onPageSizeChange(event.target.value)}
      >
        {[10, 20, 50].map((pageSize) => (
          <option key={pageSize} value={pageSize}>
            {t(($) => $.library.pagination.showCount, {
              count: pageSize,
            })}
          </option>
        ))}
        <option value={allPageSizeParamValue}>{t(($) => $.commons.all)}</option>
      </select>
    </div>
  );
};
